import type { CanvasEntity } from '../entityTypes';
import { getGroupData, getConnectorData, getTaskData } from '../entityTypes';
import type { GlobalTask } from '../../types';
import type { CanvasClipboard } from './types';

/**
 * Build a clipboard payload from the current selection.
 *
 * Miro/Lucidchart rule: internal connectors are included, external ones are dropped.
 *
 * Steps:
 * 1. Seed include-set from selectedIds
 * 2. Group expansion (recursive)
 * 3. Waypoint bridge expansion (fixed-point)
 * 4. Connector inclusion (both endpoints in set)
 * 5. GlobalTask snapshots
 * 6. Compute origin center
 */
export function buildClipboardFromSelection(
  selectedIds: string[],
  allEntities: CanvasEntity[],
  globalTasksById: Record<string, GlobalTask>,
): CanvasClipboard | null {
  if (selectedIds.length === 0) return null;

  const entityMap = new Map<string, CanvasEntity>();
  for (const e of allEntities) entityMap.set(e.id, e);

  // 1. Seed
  const includeSet = new Set<string>(selectedIds);

  // 2. Group expansion (recursive)
  let expanded = true;
  while (expanded) {
    expanded = false;
    for (const id of [...includeSet]) {
      const entity = entityMap.get(id);
      if (!entity || entity.kind !== 'group') continue;
      const gd = getGroupData(entity);
      for (const memberId of gd.memberEntityIds) {
        if (!includeSet.has(memberId)) {
          includeSet.add(memberId);
          expanded = true;
        }
      }
    }
  }

  // 3. Waypoint bridge expansion (fixed-point)
  // A waypoint not in the set becomes a "bridge" if ALL connectors referencing it
  // have their other endpoint already in the set.
  const allConnectors = allEntities.filter(e => e.kind === 'connector');
  let stable = false;
  while (!stable) {
    stable = true;
    for (const entity of allEntities) {
      if (entity.kind !== 'waypoint') continue;
      if (includeSet.has(entity.id)) continue;

      // Find all connectors that reference this waypoint
      const referencingConnectors = allConnectors.filter(c => {
        const cd = getConnectorData(c);
        return cd.from.entityId === entity.id || cd.to.entityId === entity.id;
      });

      if (referencingConnectors.length === 0) continue;

      // Check if all "other" endpoints are in the set
      const allOthersInSet = referencingConnectors.every(c => {
        const cd = getConnectorData(c);
        const otherId = cd.from.entityId === entity.id ? cd.to.entityId : cd.from.entityId;
        return includeSet.has(otherId);
      });

      if (allOthersInSet) {
        includeSet.add(entity.id);
        stable = false;
      }
    }
  }

  // 4. Connector inclusion: both endpoints must be in set
  for (const entity of allEntities) {
    if (entity.kind !== 'connector') continue;
    if (includeSet.has(entity.id)) continue;
    const cd = getConnectorData(entity);
    if (includeSet.has(cd.from.entityId) && includeSet.has(cd.to.entityId)) {
      includeSet.add(entity.id);
    }
  }

  // Collect entities
  const entities: CanvasEntity[] = [];
  for (const id of includeSet) {
    const entity = entityMap.get(id);
    if (entity) entities.push(entity);
  }

  if (entities.length === 0) return null;

  // 5. GlobalTask snapshots
  const globalTasks: Record<string, GlobalTask> = {};
  for (const entity of entities) {
    if (entity.kind === 'task') {
      const td = getTaskData(entity);
      const gt = globalTasksById[td.taskId];
      if (gt) {
        globalTasks[td.taskId] = { ...gt };
      }
    }
  }

  // 6. Origin center (bounding box of non-connector/non-waypoint entities, fallback to all)
  const positionEntities = entities.filter(e => e.kind !== 'connector');
  const forCenter = positionEntities.length > 0 ? positionEntities : entities;

  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const e of forCenter) {
    minX = Math.min(minX, e.x);
    minY = Math.min(minY, e.y);
    maxX = Math.max(maxX, e.x + e.width);
    maxY = Math.max(maxY, e.y + e.height);
  }

  const originCenter = {
    x: (minX + maxX) / 2,
    y: (minY + maxY) / 2,
  };

  return {
    entities,
    globalTasks,
    originCenter,
  };
}
